import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { EyeIcon, MagnifyingGlassIcon, CircleNotchIcon, UserIcon, PlugIcon } from "@phosphor-icons/react";

const AdminHome = ({ onConnectOwn }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const become = async (email) => {
    setBusy(email);
    try {
      await api.post("/admin/impersonate", { email });
      toast.success(`Now viewing as ${email}`);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not switch user");
      setBusy(null);
    }
  };

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  return (
    <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10 md:py-14">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#A3E635] mb-3">// Admin Console</div>
        <h1 className="font-rajdhani font-bold text-4xl md:text-5xl tracking-tight text-white text-glow">
          VIEW ANY <span className="text-[#16C784]">USER'S ACCOUNT</span>
        </h1>
        <p className="mt-4 text-[#A0A0AB] max-w-xl leading-relaxed">
          You're signed in as an admin. Pick a user below to drop into their dashboard and see everything exactly as they do.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 bg-[#050505] border border-[#222226] focus-within:border-[#16C784] px-3 py-2.5 max-w-md w-full transition-colors">
            <MagnifyingGlassIcon size={15} className="text-[#5a5a63]" />
            <input
              data-testid="admin-home-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or name"
              className="flex-1 bg-transparent text-white font-mono text-sm outline-none placeholder:text-[#3a3a40]"
            />
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-[#5a5a63]">{filtered.length} users</span>
        </div>
      </motion.div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <CircleNotchIcon size={34} className="text-[#16C784] spin-slow" />
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((u, i) => (
            <motion.button
              key={u.user_id}
              data-testid={`admin-home-user-${u.email}`}
              disabled={busy}
              onClick={() => become(u.email)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.4) }}
              className="group text-left bg-[#0C0C0E] border border-[#222226] hover:border-[#16C784]/60 hover:bg-[#16C784]/[0.04] p-4 flex items-center gap-3 transition-colors disabled:opacity-50"
            >
              {u.picture ? (
                <img src={u.picture} alt="" referrerPolicy="no-referrer" className="w-11 h-11 rounded-full border border-[#222226] shrink-0" />
              ) : (
                <div className="w-11 h-11 rounded-full border border-[#222226] flex items-center justify-center shrink-0">
                  <UserIcon size={20} className="text-[#5a5a63]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-rajdhani font-bold text-base text-white truncate">{u.name || u.email}</div>
                <div className="font-mono text-[11px] text-[#5a5a63] truncate">{u.email}</div>
                <div className="font-mono text-[10px] text-[#A3E635] mt-0.5">{u.channel_count || 0} channel{u.channel_count === 1 ? "" : "s"}</div>
              </div>
              <div className="flex items-center gap-1 text-[#5a5a63] group-hover:text-[#16C784] transition-colors shrink-0">
                {busy === u.email ? <CircleNotchIcon size={16} className="spin-slow" /> : <EyeIcon size={16} weight="fill" />}
              </div>
            </motion.button>
          ))}
        </div>
      )}

      <div className="mt-10 pt-6 border-t border-[#1a1a1e]">
        <button
          data-testid="admin-connect-own"
          onClick={onConnectOwn}
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-[#A0A0AB] hover:text-[#16C784] transition-colors"
        >
          <PlugIcon size={14} weight="fill" /> Or connect a channel to this admin account
        </button>
      </div>
    </div>
  );
};

export default AdminHome;
